// =============================================================================
// domain/exitModelBoard.ts — per-exit-model performance.
// -----------------------------------------------------------------------------
// The `SAMPLE 19  WIN 53%  EXP +$57  P&L +$1,085` row on every card of the Exit
// Model Catalog, and the strip at the top of the Edit Exit Model modal.
//
// ⚠ IT REUSES `computeLeaderboardStats` RATHER THAN RECOMPUTING. Win rate,
// expectancy and profit factor are the same questions asked of a different
// grouping, and the pattern leaderboard already encodes the two rules that are
// easy to get wrong:
//
//   • wins/losses classify on the SIGN of net P&L, not on the display unit
//   • SCRATCHES count in `sample` but not in the win-rate denominator — a day of
//     scratches is not a 0% win rate
//
// A second implementation would be a second chance to get those wrong, and the
// two numbers would disagree on screens the user reads side by side.
//
// ── ⚠ WHY THERE IS NO "UNTAGGED" ROW HERE ──
//
// The pattern leaderboard surfaces untagged trades deliberately: an untagged
// trade is a gap in the user's own review, and hiding it hides the gap. Exit
// models are different — the catalogue answers "how did each of MY exit models
// perform", and a synthetic "(no exit model)" card would sit in a list of things
// the user wrote, offering an Edit button for a row that does not exist.
//
// The count of untagged trades is still worth showing; it belongs on the tab as
// a note, not as a card. `untaggedCount` returns it.
// =============================================================================

import {
  computeLeaderboardStats, type LeaderboardStats, type UnitValue,
} from './patternLeaderboard';

export interface ExitModelTrade {
  exitModelId?: string | null;
  netPnl?: number | null;
  rrRealized?: number | null;
}

export interface ExitModelBoardRow {
  id: string;
  name: string;
  stats: LeaderboardStats;
}

/**
 * One row per exit model IN THE CATALOGUE, including models with no trades.
 *
 * ⚠ CATALOGUE-DRIVEN, exactly like the Pattern filter facet. A model the user
 * wrote but has not used yet must still appear — at zero — because the catalogue
 * is their own list of exit approaches, and one silently missing reads as
 * deleted. That is the same call, and the same reasoning, as `patternOptions`.
 *
 * ⚠ A trade tagged with an exit model that is NOT in the catalogue is dropped
 * from this board rather than given a row. The board is a view of the
 * catalogue; a dangling id is a data problem for the trade, not an exit model
 * the user can open and edit.
 */
export function exitModelBoard(
  trades: readonly ExitModelTrade[],
  catalogue: readonly { id: string; name: string }[],
  unitValue: UnitValue,
): ExitModelBoardRow[] {
  const byModel = new Map<string, ExitModelTrade[]>();
  for (const t of trades) {
    const id = t.exitModelId ? String(t.exitModelId) : '';
    if (!id) continue;
    const bucket = byModel.get(id);
    if (bucket) bucket.push(t);
    else byModel.set(id, [t]);
  }

  return catalogue.map((m) => ({
    id: m.id,
    name: m.name,
    stats: computeLeaderboardStats(byModel.get(m.id) ?? [], unitValue),
  }));
}

/** Trades carrying no exit model at all — the review gap, reported not ranked. */
export function untaggedCount(trades: readonly ExitModelTrade[]): number {
  return trades.filter((t) => !t.exitModelId).length;
}

export type ExitModelSortKey = 'sample' | 'name' | 'totalPnl' | 'winRate' | 'expectancy';

/**
 * `Sort: Sample size` is the screenshot's default, and it is the right one:
 * an exit model with three trades behind it should not head a list read as
 * evidence.
 *
 * ⚠ `Infinity` profit factors do not reach this function — none of the sort keys
 * is `profitFactor` — but the name tiebreak is here for the same reason the
 * pattern board has one: equal samples in a Map-derived order are otherwise
 * arbitrary, and a list that reshuffles between renders looks broken.
 */
export function sortExitModels(
  rows: readonly ExitModelBoardRow[], key: ExitModelSortKey,
): ExitModelBoardRow[] {
  const out = [...rows];
  out.sort((a, b) => {
    if (key === 'name') return a.name.localeCompare(b.name);
    const av = a.stats[key];
    const bv = b.stats[key];
    if (av !== bv) return bv - av;
    return a.name.localeCompare(b.name);
  });
  return out;
}
